import React, { useEffect, useState } from 'react'
import { motion } from 'framer-motion'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import Link from '@mui/material/Link'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import {
  Alert,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  InputAdornment,
} from '@mui/material'
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded'
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded'
import LockRoundedIcon from '@mui/icons-material/LockRounded'
import API from '../services/API'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import PlanSelection from './PlanSelection'
import { signupPalette } from './signupTheme'

// Steps 1–2 of the signup wizard: organisation/account details, then plan
// selection. Submitting creates the PartialSignUp (with the chosen plan) and
// sends the verification email; payment happens after email verification.
const SignUpForm = ({ showSuccessPage, setShowSuccessPage, onStepChange }) => {
  const [step, setStep] = useState(0) // 0 = details, 1 = choose plan
  const [companyName, setCompanyName] = useState('')
  const [domainName, setDomainName] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [confirmEmail, setConfirmEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [agreedTerms, setAgreedTerms] = useState(false)
  const [showTermsError, setShowTermsError] = useState(false)
  const [domainIsValid, setDomainIsValid] = useState(null)
  const [referralCode, setReferralCode] = useState('')
  const [referralValidation, setReferralValidation] = useState({
    status: 'idle',
    message: '',
  })
  const [emailMatchError, setEmailMatchError] = useState(false)
  const [passwordMatchError, setPasswordMatchError] = useState(false)
  const { executeRecaptcha } = useGoogleReCaptcha()
  const [submitError, setSubmitError] = useState('')
  const [submitLoading, setSubmitLoading] = useState(false)

  // Plan selection
  const [selectedLookupKey, setSelectedLookupKey] = useState(null)
  const [plans, setPlans] = useState([])
  const [plansLoaded, setPlansLoaded] = useState(false)

  useEffect(() => {
    onStepChange?.(step)
  }, [step, onStepChange])

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const ref = params.get('ref')

    if (ref) {
      setReferralCode(ref.trim().toUpperCase())
    }
  }, [])

  useEffect(() => {
    // Same source as the in-app billing page: live Stripe prices by lookup key
    API.get('/billing/plans')
      .then((res) => {
        if (res.status === 200 && res.data?.data) {
          setPlans(res.data.data)
        }
      })
      .finally(() => setPlansLoaded(true))
  }, [])

  useEffect(() => {
    const code = referralCode.trim()
    if (!code) {
      setReferralValidation({ status: 'idle', message: '' })
      return undefined
    }

    setReferralValidation({
      status: 'checking',
      message: 'Checking referral code…',
    })
    const controller = new AbortController()
    const timer = window.setTimeout(async () => {
      try {
        const response = await API.post(
          '/referrals/validate',
          {
            code,
            referredSubdomain: domainName,
            companyName,
            email,
          },
          { signal: controller.signal, skipGlobalErrorHandler: true },
        )
        if (response.status === 200 && response.data?.valid) {
          setReferralValidation({
            status: 'valid',
            message: 'Referral code applied.',
          })
        } else {
          setReferralValidation({
            status: 'invalid',
            message:
              response.data?.message ||
              'This referral code is not valid or eligible.',
          })
        }
      } catch (error) {
        if (error.name === 'CanceledError') return
        setReferralValidation({
          status: 'invalid',
          message: 'We could not validate this referral code. Please try again.',
        })
      }
    }, 450)

    return () => {
      window.clearTimeout(timer)
      controller.abort()
    }
  }, [referralCode, domainName, companyName, email])

  const validateDetails = () => {
    let ok = true
    setEmailMatchError(false)
    setPasswordMatchError(false)
    setSubmitError('')

    if (domainIsValid === false) ok = false
    if (referralCode.trim() && referralValidation.status !== 'valid') {
      setSubmitError('Please enter a valid referral code or leave the field blank.')
      ok = false
    }
    if (email !== confirmEmail) {
      setEmailMatchError(true)
      ok = false
    }
    if (password !== confirmPassword) {
      setPasswordMatchError(true)
      ok = false
    }
    if (!agreedTerms) {
      setShowTermsError(true)
      ok = false
    }
    return ok
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (showSuccessPage) return

    if (step === 0) {
      if (validateDetails()) {
        setStep(1)
      }
      return
    }

    // Step 1 — plan chosen, create the signup
    if (!selectedLookupKey) {
      setSubmitError('Please choose a subscription plan to continue.')
      return
    }
    setSubmitError('')

    const token = await executeRecaptcha('signup_form')

    setSubmitLoading(true)

    API.post('/new-tenant-signup', {
      companyName,
      domainName,
      firstName,
      lastName,
      email,
      password,
      lookupKey: selectedLookupKey,
      referralCode: referralCode?.trim() || undefined,
      recaptchaToken: token,
    })
      .then((res) => {
        if (res.status === 200) {
          setShowSuccessPage(true)
          return
        }
        if (res.status === 400 && res.data?.error === 'invalidPlan') {
          setSubmitError(
            res.data?.message || 'Please choose a subscription plan to continue.',
          )
          return
        }
        if (res.status === 400 && res.data?.message) {
          setReferralValidation({
            status: 'invalid',
            message:
              res.data?.message || 'This referral code is not valid or eligible.',
          })
          setSubmitError(res.data?.message || 'Invalid referral code.')
          setStep(0)
          return
        }
        if (res.status === 403) {
          setSubmitError(
            "We couldn't verify that you're human (reCAPTCHA check failed). Please refresh the page and try again.",
          )
          return
        }
        if (
          res.status === 409 ||
          (res.status === 400 && res.body === 'DomainDuplicate')
        ) {
          setDomainIsValid(false)
          setSubmitError(
            'That domain name is already taken. Please choose another.',
          )
          setStep(0)
          return
        }
        setSubmitError(
          "Oops! We were unable to create your Organisation's server. Please try again later.",
        )
      })
      .catch((e) => {
        console.log(e)
        setSubmitError(
          "Oops! We were unable to create your Organisation's server. Please try again later.",
        )
      })
      .finally(() => setSubmitLoading(false))
  }

  useEffect(() => {
    if (domainName) {
      const notAllowed = [
        'signup',
        'crmadmin',
        'api',
        'checkout',
        'billing',
        'pay',
        'buy',
        'portal',
        'mail',
        'security',
        'docs',
        'help',
        'ambunet',
        'media',
        'payments',
        'support',
      ]
      if (notAllowed.includes(domainName)) {
        setDomainIsValid(false)
        return
      }
      API.get('/domain-check?domain=' + domainName).then((res) => {
        if (res.data) {
          setDomainIsValid(true)
        } else {
          setDomainIsValid(false)
        }
      })
    } else {
      setDomainIsValid(null)
    }
  }, [domainName])

  return (
    <Grid item xs={12} id="signup" component="form" onSubmit={handleSubmit}>
      <motion.div
        key={step}
        initial={{ opacity: 0, x: 40 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.45 }}
      >
        <Box sx={{ bgcolor: 'background.paper', height: '100%' }}>
          {step === 0 ? (
            <>
              <Typography
                variant="h5"
                fontWeight={700}
                mb={0.5}
                color={signupPalette.primary.main}
              >
                Get started with AmbuNet
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Tell us about your organisation. Next you&apos;ll choose a plan,
                verify your email, accept the Business Subscription Agreement
                and complete payment — your system is provisioned as soon as
                payment succeeds.
              </Typography>
              <Stack spacing={2}>
                <TextField
                  label="Company Name"
                  fullWidth
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  required
                  autoComplete="organization"
                />
                <TextField
                  label="Domain Name"
                  fullWidth
                  value={domainName}
                  onChange={(e) => setDomainName(e.target.value)}
                  helperText={
                    domainIsValid === true
                      ? 'Available!'
                      : domainIsValid === false
                        ? 'Not Available!'
                        : `Used to generate your unique subdomain (e.g. company.${process.env.ROOT_DOMAIN}) and for SMS signatures and system references. Must be short, lowercase, and contain no spaces or special characters.`
                  }
                  error={domainIsValid === false}
                  autoComplete="none"
                  required
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        .{process.env.ROOT_DOMAIN}
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  label="Referral Code"
                  fullWidth
                  value={referralCode}
                  onChange={(e) => {
                    setReferralCode(e.target.value.toUpperCase())
                    setReferralValidation({
                      status: 'checking',
                      message: 'Checking referral code…',
                    })
                  }}
                  placeholder="Optional"
                  helperText={
                    referralValidation.message ||
                    'Optional — the code of the AmbuNet organisation that referred you.'
                  }
                  error={referralValidation.status === 'invalid'}
                  color={
                    referralValidation.status === 'valid' ? 'success' : undefined
                  }
                  focused={
                    referralValidation.status === 'valid' ? true : undefined
                  }
                  autoComplete="off"
                  InputProps={{
                    endAdornment:
                      referralValidation.status === 'checking' ? (
                        <InputAdornment position="end">
                          <CircularProgress size={20} />
                        </InputAdornment>
                      ) : undefined,
                  }}
                />
                <Stack direction={{ xs: 'column', md: 'row' }} gap={1}>
                  <TextField
                    label="Your First Name"
                    fullWidth
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                    autoComplete="given-name"
                  />
                  <TextField
                    label="Your Last Name"
                    fullWidth
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    required
                    autoComplete="family-name"
                  />
                </Stack>
                <Stack direction={{ xs: 'column', md: 'row' }} gap={1}>
                  <TextField
                    label="Email Address"
                    type="email"
                    fullWidth
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    error={emailMatchError}
                    helperText={emailMatchError && 'Emails do not match'}
                  />
                  <TextField
                    label="Confirm Email"
                    type="email"
                    fullWidth
                    value={confirmEmail}
                    onChange={(e) => setConfirmEmail(e.target.value)}
                    required
                    autoComplete="email"
                    error={emailMatchError}
                    helperText={emailMatchError && 'Emails do not match'}
                  />
                </Stack>
                <Stack direction={{ xs: 'column', md: 'row' }} gap={1}>
                  <TextField
                    label="Password"
                    type="password"
                    fullWidth
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="new-password"
                    error={passwordMatchError}
                    helperText={passwordMatchError && 'Passwords do not match'}
                    required
                  />
                  <TextField
                    label="Confirm Password"
                    type="password"
                    fullWidth
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                    error={passwordMatchError}
                    helperText={passwordMatchError && 'Passwords do not match'}
                  />
                </Stack>
                <FormControl error={showTermsError}>
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={agreedTerms}
                          onChange={(e) => {
                            setAgreedTerms(e.target.checked)
                            setShowTermsError(false)
                          }}
                        />
                      }
                      label={
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          mt={1}
                        >
                          By signing up, you agree to our{' '}
                          <Link href="/terms-of-use" target="_blank">
                            Terms of Use
                          </Link>{' '}
                          &{' '}
                          <Link href="/privacy-policy" target="_blank">
                            Privacy Policy
                          </Link>
                          .
                        </Typography>
                      }
                      required
                    />
                  </FormGroup>
                  {showTermsError && (
                    <FormHelperText>
                      You must agree to our Terms and Privacy policy.
                    </FormHelperText>
                  )}
                </FormControl>
                {submitError && <Alert severity="error">{submitError}</Alert>}
                <Button
                  variant="contained"
                  size="large"
                  type="submit"
                  endIcon={<ArrowForwardRoundedIcon />}
                  sx={{
                    bgcolor: signupPalette.primary.main,
                    '&:hover': { bgcolor: signupPalette.primary.dark },
                    textTransform: 'none',
                    fontWeight: 700,
                  }}
                  disabled={
                    Boolean(referralCode.trim()) &&
                    referralValidation.status !== 'valid'
                  }
                >
                  Continue — Choose Your Plan
                </Button>
              </Stack>
            </>
          ) : (
            <>
              <Typography
                variant="h5"
                fontWeight={700}
                mb={0.5}
                color={signupPalette.primary.main}
              >
                Choose your plan
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Pick the plan that fits your service. You&apos;ll verify your
                email next, then accept the AmbuNet Business Subscription
                Agreement and pay securely with Stripe before your system is
                created. You can upgrade at any time from within AmbuNet.
              </Typography>
              <Stack spacing={2}>
                <PlanSelection
                  value={selectedLookupKey}
                  onChange={setSelectedLookupKey}
                  plans={plans}
                  plansLoaded={plansLoaded}
                />
                {submitError && <Alert severity="error">{submitError}</Alert>}
                <Stack
                  direction={{ xs: 'column-reverse', sm: 'row' }}
                  gap={1}
                  justifyContent="space-between"
                >
                  <Button
                    size="large"
                    startIcon={<ArrowBackRoundedIcon />}
                    onClick={() => setStep(0)}
                    sx={{
                      color: signupPalette.secondary.main,
                      textTransform: 'none',
                      fontWeight: 600,
                    }}
                  >
                    Back to details
                  </Button>
                  <Button
                    variant="contained"
                    size="large"
                    type="submit"
                    startIcon={
                      submitLoading ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <LockRoundedIcon />
                      )
                    }
                    disabled={submitLoading || !selectedLookupKey}
                    sx={{
                      bgcolor: signupPalette.third.main,
                      '&:hover': { bgcolor: signupPalette.third.dark },
                      textTransform: 'none',
                      fontWeight: 700,
                      px: 3,
                    }}
                  >
                    Create Account & Verify Email
                  </Button>
                </Stack>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  textAlign="center"
                >
                  No payment is taken yet — you&apos;ll complete payment via
                  Stripe&apos;s secure checkout after verifying your email.
                </Typography>
              </Stack>
            </>
          )}
        </Box>
      </motion.div>
    </Grid>
  )
}

export default SignUpForm
